import React, { useState, useEffect, useMemo } from "react";
import "./index.scss";

import {
	Button,
	Breadcrumb,
	Alert,
	Form,
	Input,
	Layout,
	Row,
	Col,
	message,
	Flex,
	Tag,
	Card,
	Tooltip,
	Modal,
	InputNumber,
	Drawer,
	Transfer,
	Table,
	TimePicker,
	Select,
	Progress,
	Empty,
} from "antd";
import {
	RobotOutlined,
	UndoOutlined,
	SaveOutlined,
	CarOutlined,
	EditOutlined,
	DeleteOutlined,
	InfoCircleOutlined,
	LinkOutlined,
} from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { PATH } from "@/router/path.constant";
import {
	useGetAllocationDetailQuery,
	useGetAllocationByDateAndProductQuery,
	useAddAllocationMutation,
	useGenerateAiAllocationMutation,
	useDeleteAllocationMutation,
} from "@/redux/services/allocationApi";
import { useGetVehiclesByDateByProductIdQuery } from "@/redux/services/vehicleApi";
import { useGetDriverByProductAndDateQuery } from "@/redux/services/driverApi";
import difference from "lodash/difference";
import dayjs from "dayjs";

const TableTransfer = ({ leftColumns, rightColumns, ...restProps }) => (
	<Transfer titles={["已选车辆", "未选车辆"]} {...restProps}>
		{({
			direction,
			filteredItems,
			onItemSelectAll,
			onItemSelect,
			selectedKeys: listSelectedKeys,
		}) => {
			const columns = direction === "left" ? leftColumns : rightColumns;
			const rowSelection = {
				getCheckboxProps: (item) => ({
					disabled: item.disabled,
				}),
				onSelectAll(selected, selectedRows) {
					const treeSelectedKeys = selectedRows
						.filter((item) => !item.disabled)
						.map(({ key }) => key);
					const diffKeys = selected
						? difference(treeSelectedKeys, listSelectedKeys)
						: difference(listSelectedKeys, treeSelectedKeys);
					onItemSelectAll(diffKeys, selected);
				},
				onSelect({ key }, selected) {
					onItemSelect(key, selected);
				},
				selectedRowKeys: listSelectedKeys,
			};
			return (
				<Table
					rowKey={"vehicle_id"}
					rowSelection={rowSelection}
					columns={columns}
					dataSource={filteredItems}
					size="small"
					onRow={({ key, disabled: itemDisabled }) => ({
						key: key,
						onClick: () => {
							if (itemDisabled) {
								return;
							}
							onItemSelect(key, !listSelectedKeys.includes(key));
						},
					})}
				/>
			);
		}}
	</Transfer>
);

const format = "HH:mm";
const UNALLOCATED_LIST_ID = "unAllocatedList";

const getTotalPeople = (items = []) =>
	items.reduce((total, item) => total + Number(item.people_no || 0), 0);

const reorderItems = (items, sourceIndex, destinationIndex) => {
	const reorderedItems = [...items];
	const [movedItem] = reorderedItems.splice(sourceIndex, 1);
	reorderedItems.splice(destinationIndex, 0, movedItem);
	return reorderedItems;
};

// Droppable 会在拖动的每一帧重新执行 render prop。列表数据未变化时，
// 保留现有 Draggable 子树，避免长列表中的 Card、Tooltip 和 TimePicker 全部重绘。
const MemoizedDraggableList = React.memo(
	({ items, children }) => items.map(children),
	(previous, next) =>
		previous.items === next.items &&
		previous.renderVersion === next.renderVersion &&
		previous.secondaryVersion === next.secondaryVersion &&
		previous.isDragDisabled === next.isDragDisabled
);
MemoizedDraggableList.displayName = "MemoizedDraggableList";

const getPickupMapAddress = (item) => {
	if (item?.pickup_address) {
		return `${item.pickup_address}, Australia`;
	}
	if (item?.pickup_location) {
		return `${item.pickup_location}, Melbourne VIC, Australia`;
	}
	return "";
};

const groupVehicleStops = (items = []) => {
	const stops = [];
	const sortedItems = [...items].sort((a, b) =>
		(a.pickup_time || "").localeCompare(b.pickup_time || "")
	);

	sortedItems.forEach((item) => {
		const mapAddress = getPickupMapAddress(item);
		const key = `${item.pickup_time || ""}|${mapAddress.toLowerCase()}`;
		const existingStop = stops.find((stop) => stop.key === key);

		if (existingStop) {
			existingStop.passengers.push(item.client_name);
			return;
		}

		stops.push({
			key,
			pickup_time: item.pickup_time,
			pickup_location: item.pickup_location,
			pickup_address: item.pickup_address,
			map_address: mapAddress,
			passengers: [item.client_name],
		});
	});

	return stops;
};

const getRoutePoints = (stops = []) => {
	const seen = new Set();

	return stops
		.map((stop) => stop.map_address)
		.filter(Boolean)
		.filter((point) => {
			const normalizedPoint = point.toLowerCase();
			if (seen.has(normalizedPoint)) return false;
			seen.add(normalizedPoint);
			return true;
		});
};

const buildRouteMapSrc = (stops = []) => {
	const points = getRoutePoints(stops);
	if (points.length === 0) return "";
	if (points.length === 1) {
		return `https://maps.google.com/maps?q=${encodeURIComponent(
			points[0]
		)}&output=embed`;
	}

	const destinations = points.slice(1).map(encodeURIComponent).join("+to:");
	return `https://maps.google.com/maps?saddr=${encodeURIComponent(
		points[0]
	)}&daddr=${destinations}&output=embed`;
};

const buildRouteMapsLink = (stops = []) => {
	const points = getRoutePoints(stops);
	if (points.length === 0) return "";
	if (points.length === 1) {
		return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
			points[0]
		)}`;
	}

	const params = new URLSearchParams({
		api: "1",
		origin: points[0],
		destination: points[points.length - 1],
		travelmode: "driving",
	});
	if (points.length > 2) {
		params.set("waypoints", points.slice(1, -1).join("|"));
	}
	return `https://www.google.com/maps/dir/?${params.toString()}`;
};

const AllocationDetailPage = () => {
	const { Content } = Layout;
	const [messageApi, contextHolder] = message.useMessage();
	const [addAllocation] = useAddAllocationMutation();
	const [generateAiAllocation, { isLoading: aiLoading }] =
		useGenerateAiAllocationMutation();
	const [deleteAllocation] = useDeleteAllocationMutation();
	const [aiForm] = Form.useForm();
	const [aiModalOpen, setAiModalOpen] = useState(false);
	const [aiSummary, setAiSummary] = useState(null);
	const [aiFixedAssignments, setAiFixedAssignments] = useState([]);
	const [aiProgress, setAiProgress] = useState(0);
	const [routeVehicle, setRouteVehicle] = useState(null);

	const [open, setOpen] = useState(false);
	const [isDragDisabled, setIsDragDisabled] = useState(false);

	const urlParams = new URLSearchParams(window.location.search);
	const date = urlParams.get("date");
	const productId = urlParams.get("product_id");
	const [productTitle, setProductTitle] = useState("");

	const [splitValue, setSplitValue] = useState(1);
	const [openModals, setOpenModals] = useState({});

	const [recordData, setRecordData] = useState([]);
	const [allocatedLists, setAllocatedLists] = useState([]);
	const [unAllocatedList, setUnAllocatedList] = useState({
		id: UNALLOCATED_LIST_ID,
		name: "未分配",
		items: [],
	});

	const { error: detailError, refetch: refetchAllocationData } =
		useGetAllocationDetailQuery({ productId: productId, travelDate: date });

	useEffect(() => {
		if (date && productId) {
			refetchAllocationData().then((response) => {
				if (response && response.data) {
					const newData = [];
					response.data.allocationList.forEach((item) => {
						item.order_list.forEach((eachLocation) => {
							newData.push({
								order_id: item.order_id,
								order_sub_id: eachLocation.order_sub_id,
								is_allocated: item.is_allocated,
								user_name: item.user_name,
								client_name: eachLocation.client_name,
								client_phone: eachLocation.client_phone,
								option: item.option,
								comment: item.comment,
								order_status: item.status,
								pickup_location: eachLocation.pickup_location,
								pickup_address: eachLocation.pickup_address,
								people_no: eachLocation.people_no,
								pickup_time: "08:00",
							});
						});
					});

					setProductTitle(response.data.product_title);
					setRecordData(newData);
					const sortedList = [...newData].sort((item1, item2) => {
						if (item1.pickup_location < item2.pickup_location) {
							return -1;
						}
						if (item1.pickup_location > item2.pickup_location) {
							return 1;
						}
						return 0;
					});

					setUnAllocatedList((unAllocatedList) => ({
						...unAllocatedList,
						items: sortedList.filter(
							(item) => item.order_status && !item.is_allocated
						),
					}));
				}
			});
		}
	}, [date, productId, refetchAllocationData]);

	const { data: carList } = useGetVehiclesByDateByProductIdQuery({
		id: productId,
		date: date,
	});

	const originCarListWithKeys = useMemo(
		() => (carList || []).map((item) => ({
			...item,
			key: item.vehicle_id,
			allocatedSeats: 0,
			disabled: item.vehicle_status !== "available",
		})),
		[carList]
	);

	const initialTargetKeys = useMemo(
		() =>
			originCarListWithKeys.length > 0
				? originCarListWithKeys.map((item) => item.key)
				: [],
		[originCarListWithKeys]
	);
	const carById = useMemo(
		() => new Map((carList || []).map((car) => [car.vehicle_id, car])),
		[carList]
	);
	const [targetKeys, setTargetKeys] = useState(initialTargetKeys);
	const [isInit, setIsInit] = useState(false);

	useEffect(() => {
		if (initialTargetKeys.length > 0 && isInit === false) {
			setTargetKeys(initialTargetKeys);
			setIsInit(true);
		}
	}, [initialTargetKeys, isInit]);

	const [isAllocationInit, setIsAllocationInit] = useState(false);
	const { data: allocationFromDB } = useGetAllocationByDateAndProductQuery({
		productId: productId,
		travelDate: date,
	});

	const initAllocationFromDBList = allocationFromDB?.allocationList ?? [];

	useEffect(() => {
		if (
			initAllocationFromDBList.length > 0 &&
			isAllocationInit === false &&
			isInit
		) {
			const data = initAllocationFromDBList.map(
				({ list_id, list_name, vehicle, driver, ...rest }) => {
					return {
						...rest,
						id: list_id,
						vehicle_id: vehicle.vehicle_id,
						vehicle_details: vehicle,
						driver_id: driver.driver_id,
					};
				}
			);

			const carData = initialTargetKeys.filter(
				(id) => !data.some((list) => list.vehicle_id === id)
			);
			setAllocatedLists(data);
			setUnAllocatedList({
				id: "unAllocatedList",
				name: "未分配",
				items: recordData.filter(
					(item) => item.order_status && !item.is_allocated
				),
			});
			setIsAllocationInit(true);
			setIsDragDisabled(true);
			setTargetKeys(carData);
		}
	}, [initAllocationFromDBList, isAllocationInit, isInit]);

	const leftTableColumns = [
		{
			dataIndex: "vehicle_plate_number",
			title: "车牌号",
		},
		{
			dataIndex: "vehicle_model",
			title: "车型",
		},
		{
			dataIndex: "vehicle_total_seats",
			title: "总人数",
		},
		{
			dataIndex: "vehicle_available_seats",
			title: "可承载人数",
		},
		{
			dataIndex: "vehicle_color",
			title: "颜色",
		},
	];

	const rightTableColumns = [
		{
			dataIndex: "vehicle_plate_number",
			title: "车牌号",
		},
		{
			dataIndex: "vehicle_model",
			title: "车型",
		},
		{
			dataIndex: "vehicle_total_seats",
			title: "总人数",
		},
		{
			dataIndex: "vehicle_available_seats",
			title: "可承载人数",
		},
		{
			dataIndex: "vehicle_color",
			title: "颜色",
		},
	];

	const [driverList, setDriverList] = useState([]);
	const { refetch: refetchDriverData } = useGetDriverByProductAndDateQuery({
		productId: productId,
		travelDate: date,
	});

	useEffect(() => {
		if (date && productId) {
			refetchDriverData().then((response) => {
				if (response && response.data) {
					const newData = [];
					response.data.data.available_driver.forEach(
						(eachDriver) => {
							newData.push({
								value: eachDriver.driver_id,
								label: eachDriver.driver_name,
								disabled: false,
							});
						}
					);

					setDriverList(newData);
				}
			});
		}
	}, [date, productId, refetchDriverData]);

	useEffect(() => {
		if (detailError) {
			console.error("Loading Data Error:", detailError);
			messageApi.open({
				type: "error",
				content: "Loading Data Error!",
			});
		}
	}, [detailError]);

	const openModal = (order_sub_id) => {
		setSplitValue(1);
		setOpenModals({ ...openModals, [order_sub_id]: true });
	};

	const closeModal = (order_sub_id) => {
		setOpenModals({ ...openModals, [order_sub_id]: false });
	};

	const handleSplit = (order_sub_id) => {
		if (splitValue <= 0) {
			messageApi.open({ type: "error", content: "切分人数必须大于零。" });
			return;
		}

		const updatedItems = unAllocatedList.items
			.map((item) => {
				if (item.order_sub_id === order_sub_id) {
					const newSuffix = new Date().getTime(); // Increment the suffix

					const remainingPeople =
						parseInt(item.people_no) - splitValue;

					if (remainingPeople < 1) {
						messageApi.open({
							type: "error",
							content: "切分人数不能大于或等于原始人数。",
						});
						return item;
					}

					// Create a new item with the updated order ID and remaining people number
					const newItem = {
						...item,
						old_order_sub_id:
							item.old_order_sub_id === undefined
								? order_sub_id
								: item.old_order_sub_id,
						order_sub_id: `${
							order_sub_id.split("_")[0]
						}_${newSuffix}`,
						people_no: remainingPeople,
					};

					// Update the original item's people number
					const updatedItem = {
						...item,
						old_order_sub_id:
							item.old_order_sub_id === undefined
								? item.order_sub_id
								: item.old_order_sub_id,
						people_no: splitValue,
					};

					// Return both the original item with updated people number and the new item
					return [updatedItem, newItem];
				}
				return item;
			})
			.flat(); // Flatten the array of arrays into a single array

		setUnAllocatedList({ ...unAllocatedList, items: updatedItems });
		closeModal(order_sub_id); // Close the modal after splitting
	};

	const handleInputChange = (value) => {
		setSplitValue(value);
	};

	const getColorForLocation = (pickup_location) => {
		switch (pickup_location) {
			case "中心点：唐人街同仁堂":
				return "red";
			case "城北点：IBIS酒店门口":
				return "orange";
			case "西南点：靠近皇冠酒店":
				return "yellow";
			case "唐人街同仁堂":
				return "green";
			case "ACMI门口":
				return "blue";
			case "IBIS酒店":
				return "purple";
			case "君悦酒店门口":
				return "geekblue";
			case "泛太平洋酒店":
				return "pink";
			case "皇冠酒店LV专卖店门口":
				return "gray";
			case "近丽思卡尔顿酒店":
				return "maroon";
			case "近南十字街西南":
				return "navy";
			default:
				return "black";
		}
	};

	// 拖拽结束后的处理函数
	const onDragEnd = (result) => {
		if (isDragDisabled) {
			return;
		}

		const { source, destination } = result;
		if (!destination) {
			return;
		}
		if (
			source.droppableId === destination.droppableId &&
			source.index === destination.index
		) {
			return;
		}

		// 在同一个未分配列表中拖拽
		if (
			source.droppableId === UNALLOCATED_LIST_ID &&
			destination.droppableId === UNALLOCATED_LIST_ID
		) {
			setUnAllocatedList({
				...unAllocatedList,
				items: reorderItems(
					unAllocatedList.items,
					source.index,
					destination.index
				),
			});
		}

		// 在同一个已分配的列表中拖拽
		else if (
			source.droppableId === destination.droppableId &&
			source.droppableId !== UNALLOCATED_LIST_ID
		) {
			const indexToUpdate = allocatedLists.findIndex(
				(list) => list.id === source.droppableId
			);
			const updatedLists = [...allocatedLists];
			updatedLists[indexToUpdate] = {
				...updatedLists[indexToUpdate],
				items: reorderItems(
					allocatedLists[indexToUpdate].items,
					source.index,
					destination.index
				),
			};

			setAllocatedLists(updatedLists);
		}

		// 从未分配列表到已分配列表
		else if (
			source.droppableId === UNALLOCATED_LIST_ID &&
			destination.droppableId !== UNALLOCATED_LIST_ID
		) {
			const newSourceList = [...unAllocatedList.items];

			const indexToUpdate = allocatedLists.findIndex(
				(list) => list.id === destination.droppableId
			);
			const updatedList = [...allocatedLists[indexToUpdate].items];

			if (
				getTotalPeople(updatedList) +
					Number(newSourceList[source.index].people_no || 0) >
				Number(
					carById.get(allocatedLists[indexToUpdate].vehicle_id)
						?.vehicle_available_seats || 0
				)
			) {
				messageApi.open({
					type: "error",
					content: "人数已超过上限，请重新分配",
				});
			} else {
				const [sourceItem] = newSourceList.splice(source.index, 1);
				let removed = { ...sourceItem };
				setUnAllocatedList({
					...unAllocatedList,
					items: newSourceList,
				});

				const pickupTime =
					updatedList.find(
						(eachItem) =>
							eachItem.pickup_location === removed.pickup_location
					)?.pickup_time ?? null;
				if (pickupTime !== null) {
					removed = { ...removed, pickup_time: pickupTime };
				}

				updatedList.splice(destination.index, 0, removed);
				const updatedLists = [...allocatedLists];
				updatedLists[indexToUpdate] = {
					...updatedLists[indexToUpdate],
					items: updatedList,
				};
				setAllocatedLists(updatedLists);
			}
		}

		// 从已分配列表到未分配列表
		else if (
			source.droppableId !== UNALLOCATED_LIST_ID &&
			destination.droppableId === UNALLOCATED_LIST_ID
		) {
			const indexToUpdate = allocatedLists.findIndex(
				(list) => list.id === source.droppableId
			);
			const updatedList = [...allocatedLists[indexToUpdate].items];
			const [removed] = updatedList.splice(source.index, 1);
			const updatedLists = [...allocatedLists];
			updatedLists[indexToUpdate] = {
				...updatedLists[indexToUpdate],
				items: updatedList,
			};
			setAllocatedLists(updatedLists);

			const newSourceList = [...unAllocatedList.items];
			newSourceList.splice(destination.index, 0, removed);
			setUnAllocatedList({
				...unAllocatedList,
				items: newSourceList,
			});
		}

		// 在已分配的不同列表中拖拽
		else {
			const sourceIndexToUpdate = allocatedLists.findIndex(
				(list) => list.id === source.droppableId
			);
			const destinationIndexToUpdate = allocatedLists.findIndex(
				(list) => list.id === destination.droppableId
			);

			const sourceList = [...allocatedLists[sourceIndexToUpdate].items];
			const destinationList = [
				...allocatedLists[destinationIndexToUpdate].items,
			];

			// 检查是否还有空位
			if (
				getTotalPeople(destinationList) +
					Number(sourceList[source.index].people_no || 0) >
				Number(
					carById.get(
						allocatedLists[destinationIndexToUpdate].vehicle_id
					)?.vehicle_available_seats || 0
				)
			) {
				messageApi.open({
					type: "error",
					content: "人数已超过上限，请重新分配",
				});
			} else {
				var [removed] = sourceList.splice(source.index, 1);

				const pickupTime =
					destinationList.find(
						(eachItem) =>
							eachItem.pickup_location === removed.pickup_location
					)?.pickup_time ?? null;
				if (pickupTime !== null) {
					removed = { ...removed, pickup_time: pickupTime };
				}
				destinationList.splice(destination.index, 0, removed);

				const updatedLists = [...allocatedLists];
				updatedLists[sourceIndexToUpdate] = {
					...updatedLists[sourceIndexToUpdate],
					items: sourceList,
				};
				updatedLists[destinationIndexToUpdate] = {
					...updatedLists[destinationIndexToUpdate],
					items: destinationList,
				};

				setAllocatedLists(updatedLists);
			}
		}
	};

	const handleCarList = () => {
		setOpen(true);
	};

	const onClose = () => {
		setOpen(false);
	};

	const renderTitle = (item) => {
		let str = "电话: " + item.client_phone;
		if (item.comment) {
			str += ", 备注: " + item.comment;
		}
		if (item.option) {
			str += ", 附加选项: " + item.option;
		}
		return str;
	};

	const renderCarDetailsLine1 = (list) => {
		if (list.vehicle_details) {
			return `${list.vehicle_details.vehicle_model} ${list.vehicle_details.vehicle_plate_number}`;
		}
		const carDetail = carById.get(list.car_id || list.vehicle_id);
		return `${carDetail?.vehicle_model} ${carDetail?.vehicle_plate_number}`;
	};
	const renderCarDetailsLine2 = (list) => {
		if (list.vehicle_details) {
			return `最大乘客数量: ${list.vehicle_details.vehicle_available_seats}`;
		}
		const carDetail = carById.get(list.car_id || list.vehicle_id);
		return `最大乘客数量: ${carDetail?.vehicle_available_seats}`;
	};

	const renderTotalPeople = (items) => {
		return (
			"人数: " +
			getTotalPeople(items)
		);
	};

	const handleReset = () => {
		setAiSummary(null);
		setAiFixedAssignments([]);
		setTargetKeys(originCarListWithKeys.map((item) => item.key));
		setAllocatedLists([]);
		setUnAllocatedList({
			id: "unAllocatedList",
			name: "未分配",
			items: [...recordData]
				.filter((item) => item.order_status)
				.sort((item1, item2) => {
					if (item1.pickup_location < item2.pickup_location) {
						return -1;
					}
					if (item1.pickup_location > item2.pickup_location) {
						return 1;
					}
					return 0;
				}),
		});
	};

	const handleSave = async () => {
		const { isValid, reason } = onSaveValidate();
		if (isValid) {
			try {
				const requestList = allocatedLists
					.filter((item) => item.items.length > 0)
					.map(({ id, vehicle_details, ...rest }) => {
						return { ...rest, list_id: id, list_name: id };
					});
				const requestData = {
					product_id: productId,
					order_date: date,
					allocationList: requestList,
					fixed_assignments: aiFixedAssignments,
				};
				const response = await addAllocation(requestData).unwrap();
				if (response.code === "200") {
					message.success("保存成功!");
					setIsDragDisabled(true);
				} else {
					message.error("保存失败!");
				}
			} catch (error) {
				const detail = error?.data?.detail;
				const reason = Array.isArray(detail?.errors)
					? detail.errors.join("；")
					: typeof detail === "string"
						? detail
						: "保存失败!";
				message.error(reason);
			}
		} else {
			messageApi.open({
				type: "error",
				content: reason,
			});
		}
	};

	const handleAiAllocation = async (values) => {
		const fixedAssignments = (values.fixed_assignments || []).filter(
			(item) => item?.order_sub_id && item?.vehicle_id
		);

		const progressCeiling = 92 + Math.floor(Math.random() * 7);
		const progressStartedAt = Date.now();
		setAiProgress(3);
		const progressTimer = window.setInterval(() => {
			const elapsedSeconds = (Date.now() - progressStartedAt) / 1000;
			let estimatedProgress;

			if (elapsedSeconds <= 8) {
				estimatedProgress = 3 + elapsedSeconds * 4;
			} else if (elapsedSeconds <= 17) {
				estimatedProgress = 35 + (elapsedSeconds - 8) * 3.7;
			} else if (elapsedSeconds <= 25) {
				estimatedProgress = 68 + (elapsedSeconds - 17) * 2.75;
			} else {
				estimatedProgress =
					90 + Math.floor((elapsedSeconds - 25) / 4) + 1;
			}

			const nextProgress = Math.min(
				progressCeiling,
				Math.floor(estimatedProgress)
			);
			setAiProgress((current) => {
				return Math.max(current, nextProgress);
			});
		}, 1000);

		try {
			const response = await generateAiAllocation({
				product_id: productId,
				travel_date: date,
				first_pickup_time:
					values.first_pickup_time?.format(format) || "07:00",
				outbound_direction: productTitle,
				user_instructions: values.user_instructions || "",
				fixed_assignments: fixedAssignments,
			}).unwrap();
			const result = response.data;
			const suggestedLists = (result.allocationList || []).map((list) => ({
				...list,
				id: list.list_id,
				vehicle_details: carList.find(
					(vehicle) => vehicle.vehicle_id === list.vehicle_id
				),
			}));
			const usedVehicleIds = new Set(
				suggestedLists.map((list) => list.vehicle_id)
			);

			setAllocatedLists(suggestedLists);
			setUnAllocatedList({
				id: "unAllocatedList",
				name: "未分配",
				items: [],
			});
			setTargetKeys(
				originCarListWithKeys
					.filter((vehicle) => !usedVehicleIds.has(vehicle.vehicle_id))
					.map((vehicle) => vehicle.vehicle_id)
			);
			setAiFixedAssignments(fixedAssignments);
			setAiSummary(result);
			setAiProgress(100);
			setAiModalOpen(false);
			message.success("AI 分车建议已生成，请检查后保存");
		} catch (error) {
			const detail = error?.data?.detail;
			const detailText =
				typeof detail === "string" ? detail : JSON.stringify(detail || {});
			const requiresPayment = [
				"credit_balance_exhausted",
				"insufficient_quota",
				"no credits remaining",
			].some((keyword) => detailText.toLowerCase().includes(keyword));
			const reason = requiresPayment
				? "AI 接口需要付费，请充值 OpenAI API 余额后重试"
				: Array.isArray(detail?.errors)
					? detail.errors.join("；")
					: typeof detail === "string"
						? detail
						: detail?.message || "AI 分车失败，请稍后重试";
			setAiProgress(0);
			message.error(reason);
		} finally {
			window.clearInterval(progressTimer);
			window.setTimeout(() => setAiProgress(0), 500);
		}
	};

	const aiGenerating = aiLoading || (aiProgress > 0 && aiProgress < 100);
	const routeStops = useMemo(
		() => groupVehicleStops(routeVehicle?.items || []),
		[routeVehicle]
	);
	const routeMapSrc = useMemo(
		() => buildRouteMapSrc(routeStops),
		[routeStops]
	);
	const routeMapsLink = useMemo(
		() => buildRouteMapsLink(routeStops),
		[routeStops]
	);

	const handleModify = () => {
		setIsDragDisabled(false);
	};

	const handleDelete = async () => {
		try {
			const response = await deleteAllocation({
				productId: productId,
				travelDate: date,
			});
			if (response.data.code === "200") {
				message.success(`成功删除分车!`);

				setIsDragDisabled(false);
				handleReset();
			} else {
				message.success(`删除分车失败!`);
			}
		} catch (error) {
			message.error(`删除分车失败!`);
		}
	};

	const onSaveValidate = () => {
		let isValid = true;
		let reason = "";

		if (unAllocatedList.items.length > 0) {
			isValid = false;
			reason = "还有尚未分配的客户";
			return { isValid, reason };
		}

		if (
			allocatedLists.filter((item) => item.driver_id === null).length > 0
		) {
			isValid = false;
			reason = "请分配司机";
			return { isValid, reason };
		}

		return { isValid, reason };
	};

	const onCarChange = (target) => {
		if (
			originCarListWithKeys.length - target.length >
			allocatedLists.length
		) {
			// 元素从右边移动到左边 --- 添加车辆
			const notInTarget = targetKeys.filter(
				(item) => !target.includes(item)
			);
			const newLists = notInTarget.map((item, index) => ({
				id: `allocatedList_${item}`,
				vehicle_id: item,
				vehicle_details: carList.find((car) => car.vehicle_id === item),
				items: [],
				driver_id: null,
			}));

			setAllocatedLists([...allocatedLists, ...newLists]);
		} else {
			// 元素从左边移动到右边 --- 减少车辆
			const hasItemsGreaterThanZero = allocatedLists.some(
				(item) =>
					target.includes(item.vehicle_id) && item.items.length > 0
			);
			if (hasItemsGreaterThanZero) {
				messageApi.open({
					type: "error",
					content: "车辆已经分配人数，请先移除",
				});
				return;
			}

			const updatedAllocatedLists = allocatedLists.filter(
				(item) => !target.includes(item.vehicle_id)
			);
			setAllocatedLists(updatedAllocatedLists);
		}

		setTargetKeys(target);
	};

	return (
		<div className="ivt-layout">
			<Layout style={{ minHeight: "100%" }}>
				{contextHolder}
				<Content style={{ margin: "10px" }}>
					<div className="customer-container ">
						<Breadcrumb
							separator=">"
							items={[
								{
									title: PATH.home.name,
									href: PATH.home.path,
								},
								{
									title: PATH.allocation.name,
									href: PATH.allocation.path,
								},
								{
									title: date + " " + productTitle,
								},
							]}
							style={{ marginBottom: "20px" }}
						/>

						{isDragDisabled ? (
							<Flex
								align="flex-end"
								justify="flex-end"
								style={{ marginBottom: "20px" }}
							>
								<Button
									type="primary"
									icon={<DeleteOutlined />}
									size="large"
									className="new-customer-button"
									onClick={handleDelete}
									style={{
										backgroundColor: "red",
										color: "white",
									}}
								>
									删除分车
								</Button>

								<Button
									type="primary"
									icon={<EditOutlined />}
									size="large"
									className="new-customer-button"
									onClick={handleModify}
									style={{
										backgroundColor: "green",
										color: "white",
									}}
								>
									修改
								</Button>
							</Flex>
						) : (
							<Flex
								align="flex-end"
								justify="flex-end"
								style={{ marginBottom: "20px" }}
							>
								<Button
									type="primary"
									icon={<CarOutlined />}
									size="large"
									onClick={handleCarList}
									className="new-customer-button"
								>
									管理车辆
								</Button>

								<Button
									type="primary"
									icon={<RobotOutlined />}
									size="large"
									className="new-customer-button"
									onClick={() => setAiModalOpen(true)}
									style={{
										backgroundColor: "#faad14",
										borderColor: "#faad14",
										color: "white",
									}}
								>
									AI 智能分车
								</Button>

								<Button
									type="primary"
									icon={<UndoOutlined />}
									size="large"
									className="new-customer-button"
									onClick={handleReset}
									style={{
										backgroundColor: "red",
										color: "white",
									}}
								>
									重新分配
								</Button>

								<Button
									type="primary"
									icon={<SaveOutlined />}
									size="large"
									className="new-customer-button"
									onClick={handleSave}
									style={{
										backgroundColor: "green",
										color: "white",
									}}
								>
									保存
								</Button>
							</Flex>
						)}

							{aiSummary && (
								<Alert
									showIcon
									closable
									type={aiSummary.warnings?.length ? "warning" : "success"}
									message={`AI 分车建议（${aiSummary.model || "ChatGPT"}）`}
									description={
										<div>
											<div>{aiSummary.explanation}</div>
											{aiSummary.warnings?.map((warning) => (
												<div key={warning}>• {warning}</div>
											))}
										</div>
									}
									onClose={() => setAiSummary(null)}
									style={{ marginBottom: "20px" }}
								/>
							)}

						<div style={{ marginBottom: "20px" }}>
							<DragDropContext onDragEnd={onDragEnd}>
								<Row gutter={[16, 16]}>
									<Col span={18}>
										<Row gutter={[16, 16]}>
											{allocatedLists.map(
												(list, listIndex) => (
													<Col
														span={8}
														key={list.id}
														style={{
															minHeight: "500px",
														}}
													>
														<Droppable
															droppableId={
																list.id
															}
															key={list.id}
														>
															{(provided) => (
																<div
																	ref={
																		provided.innerRef
																	}
																	{...provided.droppableProps}
																	style={{
																		minHeight:
																			"500px",
																	}}
																>
																	<div
																		style={{
																			textAlign:
																				"center",
																		}}
																	>
																						<Flex
																							justify="center"
																							align="center"
																							gap={4}
																						>
																							<h3 style={{ margin: 0 }}>
																								{renderCarDetailsLine1(
																									list
																								)}
																							</h3>
																							<Tooltip title="查看行驶轨迹">
																								<Button
																									type="text"
																									shape="circle"
																									className="allocation-route-info-button"
																									icon={<InfoCircleOutlined />}
																									onClick={() => setRouteVehicle(list)}
																								/>
																							</Tooltip>
																						</Flex>
																		<h3
																			style={{
																				display:
																					"block",
																			}}
																		>
																			{renderCarDetailsLine2(
																				list
																			)}
																		</h3>
																		<h3
																			style={{
																				display:
																					"block",
																			}}
																		>
																			{renderTotalPeople(
																				list.items
																			)}
																		</h3>
																		<h3
																			style={{
																				display:
																					"block",
																			}}
																		>
																			司机:{" "}
																			<Select
																				style={{
																					width: "70%",
																				}}
																				onChange={(
																					value
																				) => {
																					const checkDriver =
																						allocatedLists.find(
																							(
																								eachItem
																							) =>
																								eachItem.driver_id ===
																								value
																						);
																					if (
																						checkDriver ===
																						undefined
																					) {
																						const updatedAllocatedLists =
																							[
																								...allocatedLists,
																							];
																						updatedAllocatedLists[
																							listIndex
																						] =
																							{
																								...updatedAllocatedLists[
																									listIndex
																								],
																								driver_id:
																									value,
																							};
																						setAllocatedLists(
																							updatedAllocatedLists
																						);
																					} else {
																						messageApi.open(
																							{
																								type: "error",
																								content:
																									"该司机已经被分配",
																							}
																						);
																					}
																				}}
																				disabled={
																					isDragDisabled
																				}
																				placeholder={
																					"请选择司机"
																				}
																				value={
																					list.driver_id
																				}
																				options={
																					driverList
																				}
																			/>
																		</h3>
																	</div>

															<MemoizedDraggableList
																items={list.items}
																renderVersion={allocatedLists}
																isDragDisabled={isDragDisabled}
															>
																{
																		(
																			item,
																			index
																		) => (
																			<Draggable
																				isDragDisabled={
																					isDragDisabled
																				}
																				key={
																					item.order_sub_id
																				}
																				draggableId={
															String(item.order_sub_id)
																				}
																				index={
																					index
																				}
																			>
																				{(
																	provided,
																	snapshot
															) => (
																<div
																	className={`allocation-draggable-item${snapshot.isDragging ? " allocation-draggable-item-dragging" : ""}`}
																						ref={
																							provided.innerRef
																						}
																						{...provided.draggableProps}
																						{...provided.dragHandleProps}
																						style={{
																							userSelect:
																								"none",
																							padding:
																								"4px",
																							...provided
																								.draggableProps
																								.style,
																						}}
																					>
																						<Tooltip
																				title={snapshot.isDragging ? null : renderTitle(item)}
																						>
																							<Card
																						className="allocation-passenger-card"
																								bordered={
																									false
																								}
																							>
																								<Row
																									gutter={[
																										8,
																										8,
																									]}
																								>
																									<Col
																								className="allocation-passenger-details"
																										span={
																											12
																										}
																									>
																										<span
																											style={{
																												fontSize:
																													"15px",
																												fontWeight:
																													"bold",
																											}}
																										>{`${item.client_name}`}</span>
																										<br />
																										{item.user_name ? (
																											<>
																												<span
																													style={{
																														fontSize:
																															"12px",
																													}}
																												>{`${
																													"分销商: " +
																														item.user_name ??
																													""
																												}`}</span>
																												<br />
																											</>
																										) : (
																											<>

																											</>
																										)}
																										{item.client_phone ? (
																											<>
																												<span
																													style={{
																														fontSize:
																															"12px",
																													}}
																												>{`${
																													"电话: " +
																														item.client_phone ??
																													""
																												}`}</span>
																												<br />
																											</>
																										) : (
																											<>

																											</>
																										)}
																										{item.comment ? (
																											<>
																												<span
																													style={{
																														fontSize:
																															"12px",
																													}}
																												>{`${
																													"备注: " +
																														item.comment ??
																													""
																												}`}</span>
																												<br />
																											</>
																										) : (
																											<>

																											</>
																										)}
																										<TimePicker
																											value={dayjs(
																												item.pickup_time,
																												format
																											)}
																											format={
																												format
																											}
																											needConfirm={
																												false
																											}
																											disabled={
																												isDragDisabled
																											}
																											onChange={(
																												value
																											) => {
																												const time =
																													value.format(
																														format
																													);
																												const newData =
																													allocatedLists[
																														listIndex
																													].items.map(
																														(
																															eachTime
																														) => {
																															if (
																																eachTime.pickup_location ===
																																item.pickup_location
																															) {
																																return {
																																	...eachTime,
																																	pickup_time:
																																		time,
																																};
																															} else {
																																return eachTime;
																															}
																														}
																													);

																												const updatedAllocatedLists =
																													[
																														...allocatedLists,
																													];
																												updatedAllocatedLists[
																													listIndex
																												] =
																													{
																														...updatedAllocatedLists[
																															listIndex
																														],
																														items: newData,
																													};
																												setAllocatedLists(
																													updatedAllocatedLists
																												);
																											}}
																										/>
																									</Col>
																									<Col
																								className="allocation-pickup-column"
																										span={
																											12
																										}
																										justify="space-between"
																										align="middle"
																									>
																										<Tag
																									className="allocation-pickup-tag"
																											color={getColorForLocation(
																												item.pickup_location
																											)}
																											style={{
																												display:
																													"flex",
																												alignItems:
																													"center",
																												justifyContent:
																													"center",
																												height: "100%",
																												width: "100%",
																												whiteSpace:
																													"normal",
																												wordWrap:
																													"break-word",
																												overflow:
																													"hidden",
																												textOverflow:
																													"ellipsis",
																											}}
																										>
																											<span
																											className="allocation-pickup-text"
																												style={{
																													fontSize:
																														"18px",
																													fontWeight:
																														"bold",
																												}}
																											>
																												人数:
																												{
																													item.people_no
																												}
																												<br />
																												<br />
																												{
																													item.pickup_location
																												}
																											</span>
																										</Tag>
																									</Col>
																								</Row>
																							</Card>
																						</Tooltip>
																					</div>
																				)}
																			</Draggable>
																							)
																					}
																				</MemoizedDraggableList>
																	{
																		provided.placeholder
																	}
																		</div>
																	)}
														</Droppable>
													</Col>
															)
														)}
										</Row>
									</Col>

									<Col span={6}>
										<Droppable
											droppableId={unAllocatedList.id}
											key={unAllocatedList.id}
										>
											{(provided) => (
														<div
													ref={provided.innerRef}
													{...provided.droppableProps}
													style={{
														minHeight: "500px",
													}}
												>
													<div
														style={{
															textAlign: "center",
														}}
													>
														<h3
															style={{
																display:
																	"block",
															}}
														>
															{
																unAllocatedList.name
															}
														</h3>
														<h3
															style={{
																display:
																	"block",
															}}
														>
															{renderTotalPeople(
																unAllocatedList.items
															)}
														</h3>
													</div>
												<MemoizedDraggableList
													items={unAllocatedList.items}
													renderVersion={openModals}
													secondaryVersion={splitValue}
													isDragDisabled={isDragDisabled}
												>
													{
														(item, index) => (
															<Draggable
																isDragDisabled={
																	isDragDisabled
																}
																key={
																	item.order_sub_id
																}
																draggableId={
													String(item.order_sub_id)
																}
																index={index}
															>
													{(provided, snapshot) => (
																	<div
															className={`allocation-draggable-item${snapshot.isDragging ? " allocation-draggable-item-dragging" : ""}`}
																		ref={
																			provided.innerRef
																		}
																		{...provided.draggableProps}
																		{...provided.dragHandleProps}
																		style={{
																			userSelect:
																				"none",
																			padding:
																				"4px",
																			...provided
																				.draggableProps
																				.style,
																		}}
																	>
																		<Tooltip
																			title={snapshot.isDragging ? null : renderTitle(item)}
																		>
																			<Card
																						className="allocation-passenger-card"
																				bordered={
																					false
																				}
																			>
																				<Row
																					gutter={[
																						8,
																						8,
																					]}
																				>
																					<Col
																								className="allocation-passenger-details"
																						span={
																							12
																						}
																					>
																						<span
																							style={{
																								fontSize:
																									"15px",
																								fontWeight:
																									"bold",
																							}}
																						>{`${item.client_name}`}</span>
																						<br />
																						{item.user_name ? (
																							<>
																								<span
																									style={{
																										fontSize:
																											"12px",
																									}}
																								>{`${
																									"分销商: " +
																										item.user_name ??
																									""
																								}`}</span>
																								<br />
																							</>
																						) : (
																							<>

																							</>
																						)}
																						{item.client_phone ? (
																							<>
																								<span
																									style={{
																										fontSize:
																											"12px",
																									}}
																								>{`${
																									"电话: " +
																										item.client_phone ??
																									""
																								}`}</span>
																								<br />
																							</>
																						) : (
																							<>

																							</>
																						)}
																						{item.comment ? (
																							<>
																								<span
																									style={{
																										fontSize:
																											"12px",
																									}}
																								>{`${
																									"备注: " +
																										item.comment ??
																									""
																								}`}</span>
																								<br />
																							</>
																						) : (
																							<>

																							</>
																						)}
																						{item.people_no >
																						1 ? (
																							<Button
																								disabled={
																									isDragDisabled
																								}
																								onClick={() =>
																									openModal(
																										item.order_sub_id
																									)
																								}
																							>
																								拆分人数
																							</Button>
																						) : (
																							<>

																							</>
																						)}
																					</Col>
																					<Col
																								className="allocation-pickup-column"
																						span={
																							12
																						}
																						justify="space-between"
																						align="middle"
																					>
																						<Tag
																									className="allocation-pickup-tag"
																							color={getColorForLocation(
																								item.pickup_location
																							)}
																							style={{
																								display:
																									"flex",
																								alignItems:
																									"center",
																								justifyContent:
																									"center",
																								height: "100%",
																								width: "100%",
																								whiteSpace:
																									"normal",
																								wordWrap:
																									"break-word",
																								overflow:
																									"hidden",
																								textOverflow:
																									"ellipsis",
																							}}
																						>
																							<span
																											className="allocation-pickup-text"
																								style={{
																									fontSize:
																										"18px",
																									fontWeight:
																										"bold",
																								}}
																							>
																								人数:
																								{
																									item.people_no
																								}
																								<br />
																								<br />
																								{
																									item.pickup_location
																								}
																							</span>
																						</Tag>
																					</Col>
																				</Row>

																				<Modal
																					title="请问需要拆分几个人"
																					open={
																						openModals[
																							item
																								.order_sub_id
																						]
																					}
																					onOk={() =>
																						handleSplit(
																							item.order_sub_id
																						)
																					}
																					onCancel={() =>
																						closeModal(
																							item.order_sub_id
																						)
																					}
																					mask={
																						false
																					}
																					maskClosable={
																						false
																					}
																					width={
																						600
																					}
																					centered
																				>
																					<InputNumber
																						min={
																							1
																						}
																						max={
																							item.people_no -
																							1
																						}
																						defaultValue={
																							splitValue
																						}
																						value={
																							splitValue
																						}
																						onChange={
																							handleInputChange
																						}
																					/>
																				</Modal>
																			</Card>
																		</Tooltip>
																	</div>
																)}
															</Draggable>
														)
													}
												</MemoizedDraggableList>
													{provided.placeholder}
												</div>
											)}
										</Droppable>
									</Col>
								</Row>
							</DragDropContext>
						</div>
					</div>
					<Modal
						title={
							routeVehicle
								? `${renderCarDetailsLine1(routeVehicle)} 行驶轨迹`
								: "车辆行驶轨迹"
						}
						open={Boolean(routeVehicle)}
						onCancel={() => setRouteVehicle(null)}
						footer={null}
						width={1080}
						destroyOnClose
					>
						{routeMapSrc ? (
							<div className="allocation-route-map-wrap">
								<iframe
									title={
										routeVehicle
											? `${renderCarDetailsLine1(routeVehicle)} route map`
											: "vehicle route map"
									}
									src={routeMapSrc}
									className="allocation-route-map"
									loading="lazy"
									referrerPolicy="no-referrer-when-downgrade"
								/>
								<div className="allocation-route-stops">
									{routeStops.map((stop, stopIndex) => (
										<div
											className="allocation-route-stop"
											key={stop.key}
										>
											<span className="allocation-route-stop-number">
												{stopIndex + 1}
											</span>
											<div>
												<strong>
													{stop.pickup_time || "--:--"} · {stop.pickup_address || stop.pickup_location}
												</strong>
												<div className="allocation-route-passengers">
													{stop.passengers.filter(Boolean).join("、")}
												</div>
											</div>
										</div>
									))}
								</div>
								<Button
									className="allocation-route-map-link"
									icon={<LinkOutlined />}
									href={routeMapsLink}
									target="_blank"
									rel="noreferrer"
								>
									打开 Google Maps
								</Button>
							</div>
						) : (
							<div className="allocation-route-map-empty">
								<Empty description="暂无可用于地图的上车地址" />
							</div>
						)}
					</Modal>
					<Modal
						title="AI 智能分车"
						open={aiModalOpen}
						onCancel={() => {
							if (!aiGenerating) setAiModalOpen(false);
						}}
						onOk={() => aiForm.submit()}
						okText="生成分车建议"
						cancelText="取消"
						confirmLoading={aiGenerating}
						closable={!aiGenerating}
						maskClosable={!aiGenerating}
						cancelButtonProps={{ disabled: aiGenerating }}
						width={760}
						destroyOnClose
					>
						<Form
							form={aiForm}
							layout="vertical"
							disabled={aiGenerating}
							onFinish={handleAiAllocation}
							initialValues={{
								first_pickup_time: dayjs("07:00", format),
								fixed_assignments: [],
							}}
						>
							{aiGenerating && (
								<Progress
									percent={aiProgress}
									status="active"
									strokeColor="#faad14"
									style={{ marginBottom: 16 }}
								/>
							)}
							<Form.Item
								name="first_pickup_time"
								label="第一位乘客接车时间"
								extra="系统会根据接送地址依次估算后续时间，并预留交通和上车缓冲。"
								rules={[{ required: true, message: "请选择第一位乘客接车时间" }]}
							>
								<TimePicker
									format={format}
									minuteStep={5}
									showNow={false}
									allowClear={false}
									needConfirm={false}
								/>
							</Form.Item>

							<Form.Item name="user_instructions" label="补充要求">
								<Input.TextArea
									rows={3}
									placeholder="例如：尽量让 Box Hill 的客人同车，先接市中心客人"
								/>
							</Form.Item>

							<Form.List name="fixed_assignments">
								{(fields, { add, remove }) => (
									<>
										<Flex justify="space-between" align="center" style={{ marginBottom: 10 }}>
											<strong>指定乘客必须使用指定车辆</strong>
											<Button disabled={aiGenerating} onClick={() => add()}>添加指定</Button>
										</Flex>
										{fields.map((field) => (
											<Row gutter={12} key={field.key}>
												<Col span={11}>
													<Form.Item
														{...field}
														name={[field.name, "order_sub_id"]}
														rules={[{ required: true, message: "请选择乘客" }]}
													>
														<Select
															placeholder="选择乘客"
															options={(Array.isArray(recordData) ? recordData : []).map((item) => ({
																value: item.order_sub_id,
																label: `${item.client_name} · ${item.pickup_location}`,
															}))}
														/>
													</Form.Item>
												</Col>
												<Col span={10}>
													<Form.Item
														{...field}
														name={[field.name, "vehicle_id"]}
														rules={[{ required: true, message: "请选择车辆" }]}
													>
														<Select
															placeholder="选择车辆"
															options={originCarListWithKeys
																.filter((vehicle) => !vehicle.disabled)
																.map((vehicle) => ({
																	value: vehicle.vehicle_id,
																	label: `${vehicle.vehicle_plate_number} · ${vehicle.vehicle_available_seats}座`,
																}))}
														/>
													</Form.Item>
												</Col>
												<Col span={3}>
											<Button danger disabled={aiGenerating} onClick={() => remove(field.name)}>删除</Button>
												</Col>
											</Row>
										))}
									</>
								)}
							</Form.List>
						</Form>
					</Modal>

					<Drawer
						title="车辆管理"
						placement="bottom"
						closable={false}
						onClose={onClose}
						open={open}
						key="bottom"
						size="large"
					>
						<TableTransfer
							dataSource={originCarListWithKeys}
							targetKeys={targetKeys}
							disabled={false}
							showSearch={false}
							onChange={onCarChange}
							leftColumns={leftTableColumns}
							rightColumns={rightTableColumns}
						/>
					</Drawer>
				</Content>
			</Layout>
		</div>
	);
};

export default AllocationDetailPage;
